
/**
 * @fileoverview Componente Atom para mostrar el contador de caracteres de un mensaje en el inbox.
 * @module MessageCounterMC
 * @category Component - Atom
 * @since 2.0.0
 * @version 3.1.0
 *
 * @description
 * Componente atómico optimizado que muestra la cantidad de caracteres escritos en un campo de mensaje,
 * permitiendo visualizar el límite máximo configurado. Utiliza memoización y gestión eficiente de estado
 * para evitar renders innecesarios. Integrado en el flujo de composición de mensajes del inbox.
 *
 * **Características:**
 * - Visualiza el conteo actual de caracteres del mensaje
 * - Muestra el límite máximo permitido
 * - Actualización reactiva mediante suscripción a métodos externos (ej: react-hook-form)
 * - Optimizado con React.memo y hooks
 *
 * **Atomic Design:**
 * - Nivel: Atom
 * - Composición: Usado en moléculas y organismos de composición de mensajes
 * - Dependencias: Ninguna dependencia visual, solo hooks de React
 *
 * @typedef {Object} MessageCounterMCProps
 * @property {Object} textareaRef - Referencia al textarea del mensaje
 * @property {number} maxChars - Límite máximo de caracteres permitidos
 *
 * @param {MessageCounterMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento JSX renderizado con el contador de caracteres
 *
 * @example
 * <MessageCounterMC textareaRef={textareaRef} maxChars={500} />
 */
import React, { memo, useState, useEffect } from "react";


/**
 * Componente atómico para mostrar el contador de caracteres de un mensaje.
 *
 * @function MessageCounterMC
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.textareaRef - Referencia al textarea del mensaje
 * @param {number} props.maxChars - Límite máximo de caracteres permitidos
 * @returns {JSX.Element} Elemento JSX renderizado
 *
 * @description
 * **Flujo de operación:**
 * 1. Se inicializa el estado local para la longitud del mensaje.
 * 2. Al montar, se suscribe a cambios en el campo indicado usando methods.watch.
 * 3. Cada vez que cambia el valor, actualiza el contador de caracteres.
 * 4. Al desmontar, limpia la suscripción para evitar memory leaks.
 *
 * **Notas de performance:**
 * - Memoizado con React.memo para evitar renders innecesarios.
 * - useCallback implícito en handlers de hooks.
 * - Limpieza automática de efectos.
 */
const MessageCounterMC = memo(({ textareaRef, maxChars }) => {
  const [messageLength, setMessageLength] = useState(0);

  useEffect(() => {
    if (!textareaRef?.current) return;

    const handleInput = () => {
      const length = textareaRef.current.value.length;
      setMessageLength(length);
    };

    textareaRef.current?.addEventListener('input', handleInput);

    return () => {
      textareaRef.current?.removeEventListener('input', handleInput);
    };
  }, [textareaRef]);

  return (
    <div className="tw-flex tw-flex-col tw-items-end tw-gap-1">
      <span className="tw-text-xs tw-text-gray-400 tw-w-14 tw-flex-shrink-0 tw-text-right">
        {messageLength}/{maxChars}
      </span>
    </div>
  );
});

MessageCounterMC.displayName = "MessageCounterMC";

export default MessageCounterMC;